import React, { useEffect, useMemo, useState } from "react";
import "./style.scss";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Select, Spin, Upload } from "antd";
import ImgCrop from "antd-img-crop";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { httpService } from "../../base/services/httpService.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";
import { FileImport, MinusButtonWithColor } from "../../assets/icons";
import { completeConsultantProfile } from "./authSlice";
import { EXPERT_STEP_KEYS } from "./RegistrationOfHumanitarian";
import SignupLayout, {
   NO_AUTOFILL,
   ArrowIcon,
   SignupStepper,
   validateSignupStep,
   wizardEnterHandler
} from "./components/SignupLayout";

const MAX_FILE_SIZE = 20971520; // 20MB

const initialValues = {
   ProfilePicture: null,
   FullName: "",
   PersonalStatement: "",
   NationalityId: null,
   ResidenceCountryId: null,
   OperatingCountriesIds: [],
   LinkedInProfile: "",
   PhoneNumber: "",
   HigestDegree: null,
   StudyFields: "",
   EducationalInstitution: "",
   Expertises: [{ areaName: "", areaExperienceYears: "" }],
   Languages: [{ languageId: "", level: "" }],
   CV: null,
   Certificate: null
};

// Fields validated before "Continue" moves on from each step. The Account step and
// e-mail verification happen on their own pages before this one, so these are the
// wizard's steps 2 (Profile) and 3 (Expertise).
const STEP_FIELDS = [
   [
      "ProfilePicture",
      "FullName",
      "PersonalStatement",
      "NationalityId",
      "ResidenceCountryId",
      "OperatingCountriesIds",
      "LinkedInProfile",
      "PhoneNumber"
   ],
   [
      "HigestDegree",
      "StudyFields",
      "EducationalInstitution",
      "Expertises",
      "Languages",
      "CV",
      "Certificate"
   ]
];

const filterByLabel = (input, option) =>
   option?.label?.toLowerCase().includes(input.toLowerCase());

const isImage = (file) => file.type === "image/png" || file.type === "image/jpeg";

/** Returns the translation key of what's wrong with a picked photo, or null if it's fine. */
const photoProblem = (file) => {
   if (!isImage(file)) return "invalid-image-file-type-validation";
   if (file.size > MAX_FILE_SIZE) return "file-size-too-large-validation";
   return null;
};

const UploadIcon = () => (
   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
         d="M12 15V4m0 0L8 8m4-4 4 4M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3"
         stroke="currentColor"
         strokeWidth="1.5"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
   </svg>
);

const PhotoIcon = () => (
   <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="15.5" cy="9.5" r="1.75" stroke="currentColor" strokeWidth="1.5" />
      <path
         d="M3 17l5-5 4 4 2.5-2.5L21 19"
         stroke="currentColor"
         strokeWidth="1.5"
         strokeLinejoin="round"
      />
   </svg>
);

/** Round photo picker: pick → crop in "Adjust your photo" → preview in the circle. */
function PhotoUpload({ file, error, onChange, onInvalid }) {
   const { t } = useTranslation();
   const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

   useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

   return (
      <ImgCrop
         cropShape="round"
         showGrid={false}
         zoomSlider
         rotationSlider={false}
         modalTitle={t("adjust-your-photo-title")}
         modalOk={t("save-button")}
         modalCancel={t("cancel")}
         // Don't open the crop window for a bad file. antd-img-crop then hands the original
         // file straight to beforeUpload, which is where it actually gets rejected.
         beforeCrop={(picked) => !photoProblem(picked)}
      >
         <Upload
            accept=".png,.jpg,.jpeg"
            showUploadList={false}
            beforeUpload={(picked) => {
               const problem = photoProblem(picked);
               if (problem) {
                  onInvalid(t(problem));
               } else {
                  onChange(picked);
               }
               return false;
            }}
         >
            <button
               type="button"
               className={`kap-photo${previewUrl ? " kap-photo--filled" : ""}${
                  error ? " kap-photo--error" : ""
               }`}
               aria-label={previewUrl ? t("change-photo-text") : t("upload-photo-text")}
            >
               {previewUrl ? (
                  <>
                     <img src={previewUrl} alt="" className="kap-photo__image" />
                     <span className="kap-photo__overlay">
                        <UploadIcon />
                        {t("change-photo-text")}
                     </span>
                  </>
               ) : (
                  <>
                     <PhotoIcon />
                     <span className="kap-photo__label">{t("upload-photo-text")}</span>
                  </>
               )}
            </button>
         </Upload>
      </ImgCrop>
   );
}

/** "Drag and drop file here, or click to select file" box for a single PDF. */
function FileDropzone({ file, onChange }) {
   const { t } = useTranslation();

   if (file) {
      return (
         <div className="kap-dropzone__file">
            <img src={FileImport} alt="" width={28} height={28} />
            <span className="kap-dropzone__file-name">{file.name}</span>
            <button type="button" className="kap-dropzone__remove" onClick={() => onChange(null)}>
               {t("remove-button")}
            </button>
         </div>
      );
   }

   return (
      <Upload.Dragger
         accept=".pdf"
         showUploadList={false}
         className="kap-dropzone"
         beforeUpload={(picked) => {
            onChange(picked);
            return false;
         }}
      >
         <UploadIcon />
         <p className="kap-dropzone__text">
            {t("drag-drop-file-here-prefix")} <span>{t("click-to-select-file-text")}</span>
         </p>
         <p className="kap-dropzone__hint">
            {t("pdf-file-type-badge")} · {t("max-file-size-text")}
         </p>
      </Upload.Dragger>
   );
}

function RegistrationOfExpertProfile() {
   const { t } = useTranslation();
   const [step, setStep] = useState(0);
   const [nationalityOptions, setNationalityOptions] = useState([]);
   const [countryOptions, setCountryOptions] = useState([]);
   const [degreeOptions, setDegreeOptions] = useState([]);
   const [languageOptions, setLanguageOptions] = useState([]);
   const [languageLevelOptions, setLanguageLevelOptions] = useState([]);
   const [loading, setLoading] = useState(false);
   // A rejected pick (wrong type / too large) never reaches Formik, so keep its message here.
   const [photoError, setPhotoError] = useState(null);
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const { executeRecaptcha } = useGoogleReCaptcha();

   const pdfFile = Yup.mixed()
      .test(
         "fileType",
         t("invalid-pdf-file-type-validation"),
         (value) => !value || value.type === "application/pdf"
      )
      .test(
         "fileSize",
         t("file-size-too-large-validation"),
         (value) => !value || value.size <= MAX_FILE_SIZE
      );

   const validationSchema = Yup.object().shape({
      ProfilePicture: Yup.mixed()
         .required(t("profile-picture-required-validation"))
         .test("fileType", t("invalid-image-file-type-validation"), (value) => !value || isImage(value))
         .test(
            "fileSize",
            t("file-size-too-large-validation"),
            (value) => !value || value.size <= MAX_FILE_SIZE
         ),
      FullName: Yup.string().required(t("full-name-required-validation")),
      NationalityId: Yup.string()
         .nullable()
         .required(t("nationality-required-validation")),
      ResidenceCountryId: Yup.string()
         .nullable()
         .required(t("country-of-residence-required-validation")),
      HigestDegree: Yup.string()
         .nullable()
         .required(t("highest-degree-required-validation")),
      Expertises: Yup.array().of(
         Yup.object().shape({
            areaName: Yup.string().trim().required(t("area-required-validation")),
            areaExperienceYears: Yup.number()
               .required(t("years-of-experience-required-validation"))
               .typeError(t("years-of-experience-number-validation"))
         })
      ),
      Languages: Yup.array().of(
         Yup.object().shape({
            languageId: Yup.string().required(t("language-required-validation")),
            level: Yup.string()
               .required(t("level-required-validation"))
               .typeError(t("level-number-validation"))
         })
      ),
      CV: pdfFile,
      Certificate: pdfFile
   });

   useEffect(() => {
      const toOptions = (items) => items.map((item) => ({ value: item.id, label: item.name }));
      const toEnumOptions = (data) =>
         Object.values(data).map((item) => ({ value: item.value, label: item.label }));

      httpService.get(`/Countries/nationalities`, (data) =>
         setNationalityOptions(toOptions(data.data))
      );
      httpService.get(`/Countries`, (data) => setCountryOptions(toOptions(data.data)));
      httpService.get(`Accounts/highest-degree`, (data) =>
         setDegreeOptions(toEnumOptions(data.data))
      );
      httpService.get(`/Countries/languages`, (data) =>
         setLanguageOptions(toOptions(data.data))
      );
      httpService.get(`/Accounts/language-level`, (data) =>
         setLanguageLevelOptions(toEnumOptions(data.data))
      );
   }, []);

   const onSubmit = async (values) => {
      if (!executeRecaptcha) {
         return;
      }

      const recaptchaToken = await executeRecaptcha("login");
      setLoading(true);
      try {
         const userId = LocalStorageService.getItem("userId");

         await dispatch(
            completeConsultantProfile(
               userId,
               {
                  id: userId,
                  FullName: values.FullName,
                  PersonalStatement: values.PersonalStatement,
                  NationalityId: values.NationalityId,
                  ResidenceCountryId: values.ResidenceCountryId,
                  OperatingCountriesIds: values.OperatingCountriesIds,
                  LinkedInProfile: values.LinkedInProfile,
                  PhoneNumber: values.PhoneNumber,
                  ProfilePicture: values.ProfilePicture,
                  HigestDegree: values.HigestDegree,
                  StudyFields: values.StudyFields,
                  EducationalInstitution: values.EducationalInstitution,
                  Expertises: values.Expertises.map((expertise) => ({
                     areaName: expertise.areaName.trim(),
                     areaExperienceYears: parseInt(expertise.areaExperienceYears, 10)
                  })),
                  Languages: values.Languages.map((language) => ({
                     languageId: String(language.languageId).trim(),
                     level: parseInt(language.level, 10)
                  })),
                  CV: values.CV ? values.CV : "",
                  Certificate: values.Certificate ? values.Certificate : ""
               },
               () => navigate("/auth/expert-registration-complete"),
               recaptchaToken
            )
         );
      } catch (error) {
         console.error("Other error:", error);
      } finally {
         setLoading(false);
      }
   };

   const goToNextStep = async (formikProps) => {
      if (await validateSignupStep(formikProps, STEP_FIELDS[step])) {
         setStep(step + 1);
         window.scrollTo(0, 0);
      }
   };

   const isLastStep = step === STEP_FIELDS.length - 1;

   const fieldError = (errors, touched, name) =>
      errors[name] && touched[name] && <div className="error-message">{errors[name]}</div>;

   // Row errors for the Expertises / Languages lists; the step check touches the whole list.
   const rowError = (errors, touched, list, index, field) =>
      errors[list]?.[index]?.[field] &&
      touched[list] && <div className="error-message">{errors[list][index][field]}</div>;

   const optionalTag = (
      <span className="kap-org-signup__optional">{t("description-optional-label")}</span>
   );

   return (
      <SignupLayout
         showcase="expert"
         {...(step === 0 ? { backTo: "/" } : { onBack: () => setStep(step - 1) })}
      >
         <p className="kap-org-signup__eyebrow">{t("signup-create-account-as-an")}</p>
         <h1 className="kap-org-signup__title">{t("profile-expert-label")}</h1>

         {/* Account (step 1) is done by the time the user lands here. */}
         <SignupStepper steps={EXPERT_STEP_KEYS.map((key) => t(key))} current={step + 1} />

         <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
         >
            {(formikProps) => {
               const { values, errors, touched, setFieldValue, setFieldTouched } = formikProps;

               const updateRow = (list, index, field, value) =>
                  setFieldValue(
                     list,
                     values[list].map((row, i) => (i === index ? { ...row, [field]: value } : row))
                  );
               const addRow = (list, emptyRow) => setFieldValue(list, [...values[list], emptyRow]);
               const removeRow = (list, index) =>
                  setFieldValue(
                     list,
                     values[list].filter((_, i) => i !== index)
                  );

               return (
                  <Form
                     className="kap-login__form"
                     style={{ maxWidth: "none" }}
                     onKeyDown={wizardEnterHandler(isLastStep, () => goToNextStep(formikProps))}
                  >
                     {step === 0 && (
                        <>
                           <p className="kap-org-signup__section">{t("profile-details-title")}</p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("profile-picture-label")}
                                 </label>
                                 <PhotoUpload
                                    file={values.ProfilePicture}
                                    error={
                                       photoError || (touched.ProfilePicture && errors.ProfilePicture)
                                    }
                                    onChange={(file) => {
                                       setPhotoError(null);
                                       setFieldValue("ProfilePicture", file);
                                       setFieldTouched("ProfilePicture", true, false);
                                    }}
                                    onInvalid={setPhotoError}
                                 />
                                 {photoError ? (
                                    <div className="error-message">{photoError}</div>
                                 ) : (
                                    fieldError(errors, touched, "ProfilePicture")
                                 )}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("full-name-label")}</label>
                                 <Field
                                    placeholder={t("enter-full-name-placeholder")}
                                    className="form-control"
                                    type="text"
                                    name="FullName"
                                    autoComplete="off"
                                 />
                                 {fieldError(errors, touched, "FullName")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("personal-statement-label")} {optionalTag}
                                 </label>
                                 <Field
                                    placeholder={t("enter-personal-statement-placeholder")}
                                    as="textarea"
                                    name="PersonalStatement"
                                    className="form-control kap-org-signup__textarea"
                                    autoComplete="off"
                                 />
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">{t("nationality-label")}</label>
                                 <Select
                                    value={values.NationalityId ?? undefined}
                                    options={nationalityOptions}
                                    showSearch
                                    autoComplete={NO_AUTOFILL}
                                    filterOption={filterByLabel}
                                    onChange={(selected) => setFieldValue("NationalityId", selected)}
                                    placeholder={t("select-nationality-placeholder")}
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "NationalityId")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("country-of-residence-label")}
                                 </label>
                                 <Select
                                    value={values.ResidenceCountryId ?? undefined}
                                    options={countryOptions}
                                    showSearch
                                    autoComplete={NO_AUTOFILL}
                                    filterOption={filterByLabel}
                                    onChange={(selected) =>
                                       setFieldValue("ResidenceCountryId", selected)
                                    }
                                    placeholder={t("select-country-of-residence-placeholder")}
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "ResidenceCountryId")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("operating-countries-label")}
                                 </label>
                                 <Select
                                    mode="multiple"
                                    value={values.OperatingCountriesIds}
                                    options={countryOptions}
                                    showSearch
                                    autoComplete={NO_AUTOFILL}
                                    filterOption={filterByLabel}
                                    onChange={(selected) =>
                                       setFieldValue("OperatingCountriesIds", selected)
                                    }
                                    placeholder={t("select-operating-countries-placeholder")}
                                    style={{ width: "100%" }}
                                 />
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("linkedin-profile-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-linkedin-profile-placeholder")}
                                    type="text"
                                    name="LinkedInProfile"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">{t("phone-number")}</label>
                                 <PhoneInput
                                    international
                                    defaultCountry="US"
                                    value={values.PhoneNumber}
                                    onChange={(value) => setFieldValue("PhoneNumber", value)}
                                    className="custom-phone-input kap-phone-input"
                                 />
                              </div>
                           </div>
                        </>
                     )}

                     {step === 1 && (
                        <>
                           <p className="kap-org-signup__section">{t("education-section-title")}</p>
                           <div className="kap-login__fields">
                              <div className="form-group">
                                 <label className="kap-login__label kap-login__label--required">
                                    {t("highest-degree-label")}
                                 </label>
                                 <Select
                                    options={degreeOptions}
                                    placeholder={t("select-highest-degree-placeholder")}
                                    value={values.HigestDegree ?? undefined}
                                    onChange={(selected) => setFieldValue("HigestDegree", selected)}
                                    style={{ width: "100%" }}
                                 />
                                 {fieldError(errors, touched, "HigestDegree")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("fields-of-study-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-fields-of-study-placeholder")}
                                    type="text"
                                    name="StudyFields"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("educational-institution-label")}
                                 </label>
                                 <Field
                                    placeholder={t("enter-educational-institution-placeholder")}
                                    type="text"
                                    name="EducationalInstitution"
                                    className="form-control"
                                    autoComplete="off"
                                 />
                              </div>
                           </div>

                           <p className="kap-org-signup__section kap-expert__section">
                              {t("areas-of-expertise-column")}
                           </p>
                           <div className="kap-expert__list">
                              {values.Expertises.map((item, index) => (
                                 <div className="kap-expert__row" key={index}>
                                    <div className="form-group">
                                       <label className="kap-login__label kap-login__label--required">{t("area-label")}</label>
                                       <input
                                          className="form-control"
                                          placeholder={t("enter-area-placeholder")}
                                          value={item.areaName}
                                          onChange={(e) =>
                                             updateRow("Expertises", index, "areaName", e.target.value)
                                          }
                                       />
                                       {rowError(errors, touched, "Expertises", index, "areaName")}
                                    </div>
                                    <div className="form-group">
                                       <label className="kap-login__label kap-login__label--required">
                                          {t("years-of-experience-column")}
                                       </label>
                                       <input
                                          className="form-control"
                                          placeholder="3"
                                          inputMode="numeric"
                                          value={item.areaExperienceYears}
                                          onChange={(e) =>
                                             updateRow(
                                                "Expertises",
                                                index,
                                                "areaExperienceYears",
                                                e.target.value.replace(/\D/g, "")
                                             )
                                          }
                                       />
                                       {rowError(
                                          errors,
                                          touched,
                                          "Expertises",
                                          index,
                                          "areaExperienceYears"
                                       )}
                                    </div>
                                    {values.Expertises.length > 1 && (
                                       <button
                                          type="button"
                                          className="kap-expert__remove"
                                          onClick={() => removeRow("Expertises", index)}
                                          aria-label={t("remove-button")}
                                       >
                                          <img src={MinusButtonWithColor} alt="" />
                                       </button>
                                    )}
                                 </div>
                              ))}
                              <button
                                 type="button"
                                 className="kap-expert__add"
                                 onClick={() =>
                                    addRow("Expertises", { areaName: "", areaExperienceYears: "" })
                                 }
                              >
                                 + {t("add-another-area-button")}
                              </button>
                           </div>

                           <p className="kap-org-signup__section kap-expert__section">
                              {t("languages-label")}
                           </p>
                           <div className="kap-expert__list">
                              {values.Languages.map((language, index) => (
                                 <div className="kap-expert__row" key={index}>
                                    <div className="form-group">
                                       <label className="kap-login__label kap-login__label--required">
                                          {t("language-label")}
                                       </label>
                                       <Select
                                          options={languageOptions}
                                          placeholder={t("select-language-placeholder")}
                                          showSearch
                                          autoComplete={NO_AUTOFILL}
                                          filterOption={filterByLabel}
                                          value={language.languageId || undefined}
                                          onChange={(selected) =>
                                             updateRow("Languages", index, "languageId", selected)
                                          }
                                          style={{ width: "100%" }}
                                       />
                                       {rowError(errors, touched, "Languages", index, "languageId")}
                                    </div>
                                    <div className="form-group">
                                       <label className="kap-login__label kap-login__label--required">{t("level-label")}</label>
                                       <Select
                                          options={languageLevelOptions}
                                          placeholder={t("select-level-placeholder")}
                                          value={language.level === "" ? undefined : language.level}
                                          onChange={(selected) =>
                                             updateRow("Languages", index, "level", selected)
                                          }
                                          style={{ width: "100%" }}
                                       />
                                       {rowError(errors, touched, "Languages", index, "level")}
                                    </div>
                                    {values.Languages.length > 1 && (
                                       <button
                                          type="button"
                                          className="kap-expert__remove"
                                          onClick={() => removeRow("Languages", index)}
                                          aria-label={t("remove-button")}
                                       >
                                          <img src={MinusButtonWithColor} alt="" />
                                       </button>
                                    )}
                                 </div>
                              ))}
                              <button
                                 type="button"
                                 className="kap-expert__add"
                                 onClick={() => addRow("Languages", { languageId: "", level: "" })}
                              >
                                 + {t("add-another-language-button")}
                              </button>
                           </div>

                           <div className="kap-login__fields kap-expert__uploads">
                              <div className="form-group">
                                 <label className="kap-login__label">{t("upload-cv-label")}</label>
                                 <FileDropzone
                                    file={values.CV}
                                    onChange={(file) => {
                                       setFieldValue("CV", file);
                                       setFieldTouched("CV", true, false);
                                    }}
                                 />
                                 {fieldError(errors, touched, "CV")}
                              </div>
                              <div className="form-group">
                                 <label className="kap-login__label">
                                    {t("upload-certificate-label")}
                                 </label>
                                 <FileDropzone
                                    file={values.Certificate}
                                    onChange={(file) => {
                                       setFieldValue("Certificate", file);
                                       setFieldTouched("Certificate", true, false);
                                    }}
                                 />
                                 {fieldError(errors, touched, "Certificate")}
                              </div>
                           </div>
                        </>
                     )}

                     {/* Distinct keys so React swaps the element instead of flipping one
                         button to type="submit" mid-click, which would submit the form. */}
                     {isLastStep ? (
                        <button
                           key="submit"
                           type="submit"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           disabled={loading}
                        >
                           {loading ? <Spin /> : t("sign-up-link")}
                        </button>
                     ) : (
                        <button
                           key="continue"
                           type="button"
                           className="btn sign-btn font-weight-bold kap-login__submit kap-org-signup__submit"
                           onClick={() => goToNextStep(formikProps)}
                        >
                           {t("continue-button")}
                           <ArrowIcon />
                        </button>
                     )}
                  </Form>
               );
            }}
         </Formik>
      </SignupLayout>
   );
}

export default RegistrationOfExpertProfile;
